/**
 * Crochet symbol charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_indic_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const crochetSymbolChartsPack: ToilePack = {
    slug: 'crochet-symbol-charts',
    category: 'craft',
    name: 'Crochet symbol charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'symbols', label: 'Symbol key', subject: 'a chart of crochet stitch symbols with each symbol beside its name and abbreviation'},
        {key: 'round', label: 'Round diagram', subject: 'a round crochet diagram for a doily drawn in stitch symbols from the centre out'},
        {key: 'granny', label: 'Granny square', subject: 'a granny square crochet diagram in symbols with the rounds numbered'},
    ],
    styles: [
        {key: 'plain', label: 'Black on white', style: 'a crisp black line symbol diagram on white paper in the style of a pattern book', palette: 'black ink only', framing: 'the diagram filling the page inside an even margin'},
        {key: 'blueprint', label: 'Blueprint', style: 'a technical blueprint drawing with white line symbols and a fine grid', palette: 'white line on deep blue', framing: 'the diagram centered on the page with a margin'},
        {key: 'rounds', label: 'Colour rounds', style: 'a pattern diagram with each round or section outlined in its own colour', palette: 'red, orange, green, teal and purple line colours on white', framing: 'the diagram filling the page inside an even margin'},
    ],
    directions: ['amigurumi', 'baby blanket', 'large print'],
    directionHint: 'try: amigurumi',
    seo: {
        title: 'Crochet Symbol Charts: Key, Round and Granny Square',
        metaDescription: 'Nine printable crochet symbol diagrams: a stitch symbol key, a round diagram and a granny square chart, in black, blueprint and colour styles.',
        h1: 'Crochet symbol charts: stitch key, round diagram, granny square',
        tagline: 'Nine diagrams in three styles, each style with a stitch key, a round diagram and a granny square chart.',
        intro: 'Crocheters read pattern diagrams in which each symbol stands for a stitch. These nine pages teach that reading: a symbol key with each symbol beside its name and abbreviation, a round diagram for a doily drawn from the centre out, and a numbered granny square chart. Each comes as a black line sheet, a blueprint or a sheet with a colour per round. Type a twist, such as amigurumi or baby blanket, and the same layouts carry it.',
        useCases: [
            'a reference card beside your hook',
            'a binder of pattern symbols',
            'a crochet class handout',
            'a craft room wall print',
        ],
        faq: [
            {q: 'Are these written crochet patterns?', a: 'No. They are symbol diagrams and a key, with no written instructions.'},
            {q: 'Is there yarn or a photograph in the pictures?', a: 'No. The black and white and blueprint pages are line symbols, with no yarn and no photographs.'},
            {q: 'What does the colour rounds style show?', a: 'Each round or section is outlined in its own colour: red, orange, green, teal or purple.'},
            {q: 'Can I ask for amigurumi or a baby blanket?', a: 'Yes. Type it as a direction. It is a request; the nine pages shown here are the ones on this page.'},
        ],
    },
}
